const REFRESH_KEY = 'tlp.pharmacy.refreshToken';

let accessToken: string | null = null;
const listeners = new Set<() => void>();

/**
 * The access token stays in memory only. Persisting it would trade a 15-minute
 * exposure window for an indefinite one, and it is cheap to re-mint from the
 * refresh token on reload. The refresh token is in localStorage because there
 * is nowhere else to put it without a same-site cookie the backend does not set
 * yet — it rotates on every use, so a stolen one is single-shot and its reuse
 * revokes the whole family server-side.
 *
 * The key is namespaced to this app. In development the two front ends sit on
 * different ports and therefore different origins anyway, but behind one host
 * in production a shared key would let a counter tablet inherit an admin
 * session, which is precisely the confusion this app exists to avoid.
 */
export const tokenStore = {
  getAccessToken: () => accessToken,
  getRefreshToken: () => localStorage.getItem(REFRESH_KEY),

  set(tokens: { accessToken: string; refreshToken: string }) {
    accessToken = tokens.accessToken;
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    emit();
  },

  clear() {
    accessToken = null;
    localStorage.removeItem(REFRESH_KEY);
    emit();
  },

  /** Notifies on sign-in and sign-out, including from another tab. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

function emit() {
  for (const listener of listeners) listener();
}

// A sign-out in one tab must not leave another tab holding a live session —
// a counter with two tabs open is the normal case, not the exception.
window.addEventListener('storage', (event) => {
  if (event.key !== REFRESH_KEY) return;
  if (event.newValue === null) {
    accessToken = null;
    emit();
  }
});
